import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import type { Product } from '@/lib/types';
import type { ComboboxOption } from '@/components/ui';
import { productSubtitle } from './b2b-service-map-rules';

/**
 * The products a service key may point at — the whole catalogue.
 *
 * 🔴 **IT EXISTS BECAUSE A MAPPING ROW CARRIES `{id, name}` AND NOTHING MORE.**
 * That is enough to print a name and not enough to check one: the catalogue's
 * names share long prefixes and differ at the tail, which is the half a picker's
 * trigger truncates away. The code and the add-on flag come from the PRODUCT, so
 * the catalogue has to be in hand wherever a mapping is DISPLAYED, not only
 * where one is chosen.
 *
 * ⚠️ **The table and the form both use this, so a product reads the same in the
 * picker as it does in the row that picker produced.** Two derivations of "which
 * product is this" would agree the day they were written and drift on the first
 * correction — on the one screen whose entire job is noticing that two similar
 * things are not the same thing.
 *
 * ⚠️ **Mounting it twice costs two reads of the same list**, and that is accepted
 * rather than overlooked: the form only mounts while it is open, and the
 * alternative is threading the records down from the page, where a stale copy
 * handed to a form would be a mapping saved against a list nobody refreshed.
 */
export interface B2bServiceProductsState {
  /** For a picker. Built from the records, in the order they arrived. */
  options: ComboboxOption[];
  /** For a table: the full record behind a mapping's `{id, name}`. */
  byId: ReadonlyMap<number, Product>;
  loading: boolean;
  /** The server's own sentence. `null` while a read is in flight or after one succeeds. */
  error: string | null;
}

/**
 * 🔴 **THE CATALOGUE IS READ IN FULL, PAGE BY PAGE, AND A SHORT READ THROWS.**
 * `listAllProducts` is the house answer and it refuses to resolve short.
 *
 * ⚠️ **IT FITS IN ONE PAGE TODAY AND THAT IS NOT A REASON TO READ ONE PAGE** —
 * that method's own doc makes the argument at length, and this screen is exactly
 * the caller it is aimed at: **a product missing because it fell past a page
 * cut-off is indistinguishable from a product that does not exist**, and the
 * person who cannot find it maps the service to the plausible one sitting where
 * it should have been. Truncation here is silent, and a wrong mapping is a
 * proposal's service resolving to the wrong product with nothing anywhere
 * disagreeing.
 *
 * ⚠️ **NO `active` FILTER, AND THAT IS NOT AN OMISSION.** A picker normally
 * offers only what may be CHOSEN — but a mapping outlives a catalogue decision,
 * so a live row can legitimately point at a product SEED no longer sells.
 * Narrowing to active would blank the trigger on exactly those rows, the next
 * save would reassign them to whatever was picked instead, and the TABLE would
 * lose the marker on the very rows most worth looking at. ⛔ Retired products are
 * INCLUDED and LABELLED, never filtered out.
 *
 * ⚠️ **The filter would be spelled `active`, not `isActive`**, if one were ever
 * wanted: the catalog resources do not follow the reference masters' convention
 * and their query schema is `.strict()`, so the masters' spelling is a 422
 * naming the key.
 */
export function useB2bServiceProducts(): B2bServiceProductsState {
  const [records, setRecords] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((signal: AbortSignal) => {
    setLoading(true);
    return api
      .listAllProducts(signal)
      .then((rows) => {
        setRecords(rows);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * ⛔ The list is EMPTIED rather than left holding a stale one. A picker
         * still offering yesterday's options beside a failure message invites a
         * save against a list nobody can vouch for.
         */
        setRecords([]);
        setError(caught instanceof Error ? caught.message : 'The product catalogue could not be read.');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const byId = useMemo(() => {
    const map = new Map<number, Product>();
    for (const record of records) map.set(record.id, record);
    return map;
  }, [records]);

  const options = useMemo<ComboboxOption[]>(
    () =>
      records.map((record) => {
        const subtitle = productSubtitle(record);
        /*
         * ⚠️ **The hint carries the discriminator and is SEARCHABLE**, which is
         * what lets somebody type a product code and land on the right row among
         * several sharing a prefix. An option's `hint` labels an option and has
         * never been in scope for the no-helper-text rule.
         */
        const hintParts = [subtitle, record.productActive ? '' : 'no longer offered'].filter(
          Boolean,
        );
        return {
          value: String(record.id),
          label: record.productName,
          ...(hintParts.length > 0 ? { hint: hintParts.join(' · ') } : {}),
        };
      }),
    [records],
  );

  /*
   * ⚠️ **A stored id absent from the list is NOT injected back as a bare
   * option.** Everything is offered already — active and retired alike — and
   * `f_b2b_proposal_service_map.product_id` is `ON DELETE RESTRICT`, so a mapped
   * product cannot be deleted out from under its row. A synthesised option
   * reading `12` would therefore be describing a state the schema forbids, while
   * letting somebody re-save a mapping onto it.
   */

  return { options, byId, loading, error };
}
